import { createSlice } from "@reduxjs/toolkit";

export const AI_TASK_TTL_MS = 30 * 60 * 1000;

const getTaskTime = (task) => {
    const timestamp = Date.parse(task?.startedAt || "");
    return Number.isFinite(timestamp) ? timestamp : 0;
};

export const isAiTaskExpired = (task, now = Date.now(), ttlMs = AI_TASK_TTL_MS) => {
    const startedAt = getTaskTime(task);
    if (!startedAt) return true;
    return now - startedAt > ttlMs;
};

const getActiveScopeSet = (options = {}) => {
    const activeScopeIds = options.activeScopeIds;
    if (!Array.isArray(activeScopeIds)) return null;
    return new Set(
        activeScopeIds
            .filter((scopeId) => scopeId !== null && scopeId !== undefined)
            .map((scopeId) => String(scopeId))
    );
};

const normalizeTask = (task = {}) => {
    const id = String(task.id || "").trim();
    if (!id) return null;

    return {
        id,
        kind: String(task.kind || "ai_task"),
        label: String(task.label || ""),
        scopeId: task.scopeId == null ? null : String(task.scopeId),
        blocking: task.blocking !== false,
        startedAt: task.startedAt || new Date().toISOString(),
    };
};

const aiTasksSlice = createSlice({
    name: "aiTasks",
    initialState: {
        activeTasks: {},
    },
    reducers: {
        startAiTask(state, action) {
            const task = normalizeTask(action.payload);
            if (!task) return;
            state.activeTasks[task.id] = task;
        },
        finishAiTask(state, action) {
            const id = typeof action.payload === "object"
                ? action.payload?.id
                : action.payload;
            if (!id) return;
            delete state.activeTasks[String(id)];
        },
        clearAiTasksByScope(state, action) {
            const scopeId = typeof action.payload === "object"
                ? action.payload?.scopeId
                : action.payload;
            if (scopeId == null) return;
            const target = String(scopeId);
            Object.entries(state.activeTasks).forEach(([id, task]) => {
                if (String(task.scopeId) === target) {
                    delete state.activeTasks[id];
                }
            });
        },
        clearExpiredAiTasks(state, action) {
            const now = action.payload?.now || Date.now();
            const ttlMs = action.payload?.ttlMs || AI_TASK_TTL_MS;
            Object.entries(state.activeTasks).forEach(([id, task]) => {
                if (isAiTaskExpired(task, now, ttlMs)) {
                    delete state.activeTasks[id];
                }
            });
        },
        clearAllAiTasks(state) {
            state.activeTasks = {};
        },
    },
});

export const {
    startAiTask,
    finishAiTask,
    clearAiTasksByScope,
    clearExpiredAiTasks,
    clearAllAiTasks,
} = aiTasksSlice.actions;

export const selectActiveAiTasks = (state, options = {}) => {
    const now = options.now || Date.now();
    const ttlMs = options.ttlMs || AI_TASK_TTL_MS;
    const activeScopeSet = getActiveScopeSet(options);

    return Object.values(state.aiTasks?.activeTasks || {}).filter((task) => {
        if (isAiTaskExpired(task, now, ttlMs)) return false;
        if (activeScopeSet && !activeScopeSet.has(String(task.scopeId))) return false;
        return true;
    });
};

export const selectBlockingAiTasks = (state, options = {}) =>
    selectActiveAiTasks(state, options).filter((task) => task.blocking !== false);

export const selectHasBlockingAiTask = (state, options = {}) =>
    selectBlockingAiTasks(state, options).length > 0;

export default aiTasksSlice.reducer;
